/**
 * Variant Selector Controller Core (Phase B implementation)
 *
 * Extracted from the inline <script> block in shopify/blocks/size-advisor.liquid.
 * Provides stateless pricing helpers + a Controller factory per spec §5.
 *
 * Specification:
 *   docs/superpowers/specs/2026-04-20-variant-selector-modular-restructure-design.md §5
 * Plan:
 *   docs/superpowers/plans/2026-04-21-variant-selector-restructure-and-flyout-removal.md §5 (Phase B)
 *
 * Mode-specific rendering (size cards, weight slider, variation pills, etc.)
 * is NOT part of this controller — it lives inline in each mode snippet.
 */
(function () {
  'use strict';

  var reducedMotionQuery = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function galleryScrollBehavior() {
    return reducedMotionQuery && reducedMotionQuery.matches ? 'instant' : 'smooth';
  }

  // ---------------------------------------------------------------------------
  // Stateless helpers
  // ---------------------------------------------------------------------------

  /**
   * Format a cent value as a localized money string. Uses
   * Shopify.formatMoney when available, otherwise falls back to a German
   * "12,34 €" rendering.
   *
   * @param {number} cents
   * @param {string} [moneyFormat] - e.g. '{{amount_with_comma_separator}} €'
   * @returns {string}
   */
  function fmt(cents, moneyFormat) {
    if (typeof cents !== 'number') cents = Number(cents) || 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  /**
   * Format a Grundpreis (per-unit price) as e.g. "0,12 EUR* per 100 g".
   * Output uses the literal asterisk-slash pair (see PAngV conventions);
   * we spell it out in this JSDoc because the literal sequence would end
   * this block comment early.
   *
   * @param {number} cents
   * @param {string} unit - e.g. '100 g'
   * @returns {string}
   */
  function formatUnitPriceCents(cents, unit) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €*/' + (unit || '');
  }

  /**
   * Read the PIM-based Grundpreis base from a variant bridge payload.
   * The theme never reads variant.unit_price_measurement (see
   * .claude/rules/unit-price-measurement.md).
   *
   * @param {object} variant - bridge shape with pimUnitPrice { price, unit }
   * @returns {{price:number, unit:string}|null}
   */
  function getUnitPriceBase(variant) {
    if (!variant || !variant.pimUnitPrice || !variant.pimUnitPrice.price) return null;
    return { price: variant.pimUnitPrice.price, unit: variant.pimUnitPrice.unit || '' };
  }

  /**
   * Return the Grundpreis string for a variant at its nominal price.
   *
   * @param {object} variant
   * @returns {string|null}
   */
  function getUnitPriceText(variant) {
    var base = getUnitPriceBase(variant);
    return base ? formatUnitPriceCents(base.price, base.unit) : null;
  }

  /**
   * Return the Grundpreis string scaled by an effective per-unit price
   * (e.g. after quantity/subscription discount). The Grundpreis is linear
   * in the variant price, so scaling the base cents by the same ratio
   * yields the correct discounted Grundpreis without re-deriving the
   * measurement from Shopify-native fields.
   *
   * @param {object} variant
   * @param {number} effectivePriceCents
   * @returns {string|null}
   */
  function getUnitPriceTextScaled(variant, effectivePriceCents) {
    var base = getUnitPriceBase(variant);
    if (!base || !variant.price) return null;
    var ratio = effectivePriceCents / variant.price;
    return formatUnitPriceCents(Math.round(base.price * ratio), base.unit);
  }

  /**
   * Return the highest discount percent matching the given quantity.
   * Tiers: [{ min: <int>, percent: <int> }, ...]. Returns 0 when no tier
   * applies or tiers are missing. This is display-only preview data for
   * Shopify-native automatic discounts; cart and checkout remain the source
   * of truth.
   *
   * @param {number} qty
   * @param {Array<{min:number,percent:number}>} tiers
   * @returns {number}
   */
  function getDiscountPercent(qty, tiers, subscription) {
    if (!tiers || !Array.isArray(tiers)) return 0;
    var pct = 0;
    tiers.forEach(function (tier) {
      var applies = subscription ? tier.appliesOnSubscription !== false : tier.appliesOnOneTimePurchase !== false;
      if (applies && qty >= tier.min) pct = Math.max(pct, tier.percent);
    });
    return pct;
  }

  /**
   * Percentage fallback for legacy selling-plan bridges only. Current bridges
   * supply Shopify's native selling_plan_allocation.price instead.
   *
   * @param {number} cents
   * @param {number} percent
   * @returns {number}
   */
  function applyPercentDiscount(cents, percent) {
    cents = Number(cents) || 0;
    percent = Number(percent) || 0;
    return percent > 0 ? Math.round(cents * (100 - percent) / 100) : cents;
  }

  // Native automatic product discount: truncate the discount per unit, then
  // multiply. Verified against this store (931 cents, 5%, qty 2 => 1770).
  // Do not round a combined quantity/subscription percentage on the line.
  function quantityDiscountedTotal(unitCents, quantity, percent) {
    var discount = Math.floor(Number(unitCents) * (Number(percent) || 0) / 100 + 1e-9);
    return (Number(unitCents) - discount) * Number(quantity);
  }

  /**
   * Parse and sanitize display-only quantity-discount preview tiers. Accepts
   * either an array or a JSON string for legacy integrations; native consumers
   * receive the verified read model asynchronously.
   *
   * @param {Array|string|null|undefined} raw
   * @returns {Array<{min:number,percent:number}>}
   */
  function parseDiscountTiers(raw) {
    var parsed = raw;
    if (typeof raw === 'string') {
      if (!raw.trim()) return [];
      try {
        parsed = JSON.parse(raw);
      } catch (_err) {
        return [];
      }
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(function (tier) {
        var value = {
          min: Number(tier && tier.min),
          percent: Number(tier && tier.percent)
        };
        if (tier && typeof tier.appliesOnSubscription === 'boolean') value.appliesOnSubscription = tier.appliesOnSubscription;
        if (tier && typeof tier.appliesOnOneTimePurchase === 'boolean') value.appliesOnOneTimePurchase = tier.appliesOnOneTimePurchase;
        return value;
      })
      .filter(function (tier) {
        return Number.isFinite(tier.min) &&
          Number.isFinite(tier.percent) &&
          Number.isInteger(tier.min) && tier.min > 1 &&
          tier.percent > 0 &&
          tier.percent <= 100;
      })
      .sort(function (a, b) { return a.min - b.min; });
  }

  // ---------------------------------------------------------------------------
  // Controller factory
  // ---------------------------------------------------------------------------

  /**
   * Create a Controller bound to a specific [data-variant-selector]
   * container. Owns variant/quantity/selling-plan state, computes
   * effective prices (quantity + subscription discount), renders the
   * shared atoms (quantity list, price, ATC button), and exposes a
   * minimal event bus for mode snippets to subscribe to.
   *
   * Mode snippets remain responsible for rendering mode-specific UI
   * (size cards, variation pills, weight slider, etc.) and for wiring
   * those UI elements to controller.selectVariant() / setQuantity() /
   * setSellingPlan().
   *
   * @param {HTMLElement} container
   * @param {object} options
   * @param {Array} options.variants - bridge payload from [data-vs-variants]
   * @param {Array|string|null} [options.discountTiers] - legacy preview tiers; ignored by native consumers
   * @param {object|null} [options.profile] - SML profile (mode-specific, unused by controller)
   * @param {string} [options.moneyFormat] - shop.money_with_currency_format
   * @param {object} [options.translations] - { addToCart, unavailable, ... }
   * @param {Array} [options.sellingPlanGroups]
   * @param {object} [options.flags] - { showUnitPrice, customQtyMax, freeShippingThreshold, ... }
   * @returns {object} controller instance
   */
  function createController(container, options) {
    if (!container) throw new Error('[VariantSelector] createController: container is required');
    options = options || {};
    var variants = Array.isArray(options.variants) ? options.variants : [];
    var nativeDiscounts = container.hasAttribute('data-native-quantity-discounts');
    var discountTiers = nativeDiscounts ? [] : parseDiscountTiers(options.discountTiers);
    var profile = options.profile || null;
    var moneyFormat = options.moneyFormat || '';
    var translations = options.translations || {};
    var flags = options.flags || {};
    var showUnitPrice = !!flags.showUnitPrice;
    var showCustomQtyPrice = !!flags.showCustomQtyPrice;
    var showQtyReach = !!flags.showQtyReach;
    var customQtyMax = Number(flags.customQtyMax) || 20;
    function qualifiesForShipping(variant, total) {
      // A PDP cannot know later cart/order discounts. The total-cart policy
      // therefore shows its condition instead of promising free delivery.
      return variant.shippingPolicy !== 'de-cart-total' && !state.sellingPlanId && Number(variant.shippingThreshold) > 0
        && Number(variant.shippingExpiresAt) * 1000 > Date.now()
        && total >= Number(variant.shippingThreshold);
    }
    var freeShippingBadgeShow = flags.freeShippingBadgeShow !== false; // default on
    var freeShippingBadgeLabel = flags.freeShippingBadgeLabel || 'Versandkostenfrei';
    var freeShippingBadgePosition = flags.freeShippingBadgePosition || 'right_of_title';

    var QTY_PRESET_MAX = 3;

    var state = {
      selectedVariantId: null,
      quantity: 1,
      sellingPlanId: null,
      isSubscription: false,
      isFallbackMode: false
    };
    var quantityInputBlocked = false;
    var atcPending = false;
    var atcRequest = null;
    var atcResetTimer = null;

    // --- Event bus -----------------------------------------------------------
    var listeners = {};
    function on(event, cb) {
      if (!listeners[event]) listeners[event] = [];
      listeners[event].push(cb);
    }
    function off(event, cb) {
      var arr = listeners[event];
      if (!arr) return;
      for (var i = arr.length - 1; i >= 0; i--) {
        if (arr[i] === cb) arr.splice(i, 1);
      }
    }
    function emit(event, data) {
      var arr = listeners[event];
      if (!arr) return;
      for (var i = 0; i < arr.length; i++) {
        try { arr[i](data); } catch (e) { /* keep other subscribers alive */ console.error('[VariantSelector] listener error:', e); }
      }
    }

    // --- DOM refs (lazily re-queried; atoms may be rendered after init) -----
    function query(selector) { return container.querySelector(selector); }

    function formatStatusMessage(template, values) {
      var message = template || '';
      var replacements = values || {};
      Object.keys(replacements).forEach(function (key) {
        message = message.split('__' + key + '__').join(String(replacements[key]));
      });
      return message;
    }

    function getQuantityInvalidMessage() {
      return container.dataset.vsQuantityInvalidMessage
        || translations.quantityInvalid
        || 'Enter a whole quantity between 1 and __MAX__.';
    }

    function getQuantityAdjustedMessage() {
      return container.dataset.vsQuantityAdjustedMessage
        || translations.quantityAdjusted
        || 'The available quantity was adjusted to __QUANTITY__.';
    }

    function getCartErrorMessage() {
      return container.dataset.vsCartErrorMessage
        || translations.cartError
        || 'The product could not be added to the cart. Please try again.';
    }

    function getCartAddUrl() {
      if (container.dataset.vsCartAddUrl) {
        var configuredUrl = String(container.dataset.vsCartAddUrl);
        return /\/cart\/add\/?$/.test(configuredUrl)
          ? configuredUrl.replace(/\/$/, '') + '.js'
          : configuredUrl;
      }
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? String(window.Shopify.routes.root)
        : '/';
      if (root.charAt(root.length - 1) !== '/') root += '/';
      return root + 'cart/add.js';
    }

    function setStatus(statusEl, message, isError) {
      if (!statusEl) return;
      statusEl.textContent = message || '';
      statusEl.classList.toggle('vs__status--error', !!message && !!isError);
    }

    function setQuantityStatus(message, isError) {
      var input = query('[data-vs-qty-custom]') || query('[data-sml-qty-custom]');
      var wrapper = query('[data-vs-qty-custom-wrap]')
        || query('[data-sml-qty-custom-wrapper]')
        || (input && input.parentElement);
      if (input) input.setAttribute('aria-invalid', isError ? 'true' : 'false');
      if (wrapper) wrapper.classList.toggle('is-invalid', !!message && !!isError);
      setStatus(query('[data-vs-quantity-status]'), message, isError);
    }

    function clearQuantityValidation(clearStatus) {
      quantityInputBlocked = false;
      var input = query('[data-vs-qty-custom]') || query('[data-sml-qty-custom]');
      var wrapper = query('[data-vs-qty-custom-wrap]')
        || query('[data-sml-qty-custom-wrapper]')
        || (input && input.parentElement);
      if (input) input.setAttribute('aria-invalid', 'false');
      if (wrapper) wrapper.classList.remove('is-invalid');
      if (clearStatus !== false) setStatus(query('[data-vs-quantity-status]'), '', false);
    }

    function setAtcStatus(message, isError) {
      var status = query('[data-vs-atc-status]');
      setStatus(status, message, isError);
      window.CartAdd?.showRecovery?.(status);
    }

    function clearCustomQuantityPrices() {
      var sharedPrice = query('[data-vs-custom-qty-price]') || query('[data-sml-qty-custom-price]');
      var pickerPrice = query('[data-vs-qty-custom-prices]');
      if (sharedPrice) sharedPrice.innerHTML = '';
      if (pickerPrice) pickerPrice.innerHTML = '';
    }

    function syncAtcDisabled() {
      var atc = query('[data-vs-add-to-cart]') || query('[data-sml-add-to-cart]');
      if (!atc) return;
      var v = getSelectedVariant();
      atc.disabled = atcPending || !!window.CartOperations?.isBlocked()
        || quantityInputBlocked || getEffectiveMax() < 1 || !(v && v.purchasable);
    }

    // --- Selected variant lookup --------------------------------------------
    function findVariant(id) {
      id = Number(id);
      if (!id) return null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) return variants[i];
      }
      return null;
    }
    function getSelectedVariant() {
      if (state.selectedVariantId == null) return null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === state.selectedVariantId) return variants[i];
      }
      return null;
    }

    // --- Subscription discount lookup (mirrors legacy size-advisor logic) ---
    // Subscription-selector cards embed data-plan-discount on each frequency
    // button. We prefer the button matching the active sellingPlanId and fall
    // back to the --active button so discount still reflects the visible UI.
    function getSubscriptionDiscount(planId) {
      var subContainer = query('[data-sml-subscription]');
      if (!subContainer) return 0;
      if (!planId) return 0;
      var btn = subContainer.querySelector('.subscription-frequency-btn[data-selling-plan-id="' + planId + '"]')
        || subContainer.querySelector('[data-frequency-select] option[value="' + planId + '"]');
      return btn ? (Number(btn.dataset.planDiscount) || 0) : 0;
    }

    // Preview discount — shown on the subscription card even when the user
    // currently has Einmalkauf selected, so the savings are still visible.
    function getSubscriptionPreviewId() {
      var subContainer = query('[data-sml-subscription]');
      if (!subContainer) return null;
      var select = subContainer.querySelector('[data-frequency-select]');
      if (select) return Number(select.value) || null;
      var btn = subContainer.querySelector('.subscription-frequency-btn--active')
        || subContainer.querySelector('.subscription-frequency-btn');
      return btn ? (Number(btn.dataset.sellingPlanId) || null) : null;
    }

    // --- Effective price ----------------------------------------------------
    function getEffectivePrice() {
      return getPriceForQuantity(state.quantity, state.sellingPlanId);
    }

    // Shared contract for quantity rows, purchase cards, custom quantity and
    // CTA/sticky snapshot. Explicit null means one-time; omitted means active.
    function getPriceForQuantity(quantity, planId) {
      if (planId === undefined) planId = state.sellingPlanId;
      var v = getSelectedVariant();
      if (!v) {
        return {
          unitCents: 0,
          totalCents: 0,
          nominalTotalCents: 0,
          qtyDiscountedTotalCents: 0,
          discountPct: 0,
          unitPriceText: null
        };
      }
      var qty = Math.max(1, Number(quantity) || 1);
      var qtyPct = getDiscountPercent(qty, discountTiers, !!planId);
      var nominalTotal = v.price * qty;
      var qtyDiscountedTotal = quantityDiscountedTotal(v.price, qty, qtyPct);
      var subDiscount = getSubscriptionDiscount(planId);
      var purchaseUnitPrice = v.price;
      if (planId) {
        var allocation = (v.sellingPlanAllocations || []).find(function (entry) {
          return Number(entry.id) === Number(planId);
        });
        purchaseUnitPrice = allocation ? Number(allocation.price) : applyPercentDiscount(v.price, subDiscount);
      }
      var total = quantityDiscountedTotal(purchaseUnitPrice, qty, qtyPct);
      var unitPrice = Math.round(total / qty);
      // Combined discount vs nominal price (used for label hints).
      var combinedPct = nominalTotal > 0 ? Math.round((1 - total / nominalTotal) * 100) : 0;
      return {
        unitCents: unitPrice,
        totalCents: total,
        nominalTotalCents: nominalTotal,
        purchaseTotalCents: purchaseUnitPrice * qty,
        qtyDiscountedTotalCents: qtyDiscountedTotal,
        discountPct: combinedPct,
        qtyDiscountPct: qtyPct,
        subscriptionDiscountPct: subDiscount,
        unitPriceText: showUnitPrice ? getUnitPriceTextScaled(v, unitPrice) : null
      };
    }

    function getPurchasePricePayload() {
      var v = getSelectedVariant();
      if (!v) return null;
      var pricesByPlan = {};
      (v.sellingPlanAllocations || []).forEach(function (allocation) {
        pricesByPlan[allocation.id] = getPriceForQuantity(state.quantity, allocation.id).totalCents;
      });
      var subContainer = query('[data-sml-subscription]');
      if (subContainer) {
        subContainer.querySelectorAll('.subscription-frequency-btn, [data-frequency-select] option').forEach(function (node) {
          var id = node.dataset.sellingPlanId || node.value;
          if (id) pricesByPlan[id] = getPriceForQuantity(state.quantity, id).totalCents;
        });
      }
      return {
        variantId: state.selectedVariantId,
        quantity: state.quantity,
        price: getPriceForQuantity(state.quantity, null).totalCents,
        pricesByPlan: pricesByPlan
      };
    }

    // --- Public storefront state snapshot ---------------------------------
    // Resolve the active selling-plan label from the optional controller data
    // first, then from the subscription UI rendered inside this selector. The
    // latter is the current theme contract for both cards and compact modes.
    function getSellingPlanName() {
      if (!state.sellingPlanId) return null;
      var planId = Number(state.sellingPlanId);
      var groups = Array.isArray(options.sellingPlanGroups) ? options.sellingPlanGroups : [];

      for (var i = 0; i < groups.length; i++) {
        var group = groups[i] || {};
        var plans = group.sellingPlans || group.selling_plans || group.plans || [];
        for (var j = 0; j < plans.length; j++) {
          var plan = plans[j] || {};
          if (Number(plan.id) === planId && plan.name) return String(plan.name);
        }
      }

      var subContainer = query('[data-sml-subscription]');
      if (!subContainer) return null;

      var planButton = subContainer.querySelector(
        '.subscription-frequency-btn[data-selling-plan-id="' + String(planId) + '"]'
      );
      if (planButton) {
        var buttonName = planButton.dataset.planName || planButton.textContent;
        if (buttonName && buttonName.trim()) return buttonName.trim();
      }

      var frequencySelect = subContainer.querySelector('[data-frequency-select]');
      if (frequencySelect) {
        for (var k = 0; k < frequencySelect.options.length; k++) {
          var option = frequencySelect.options[k];
          if (Number(option.value) !== planId) continue;
          var optionName = option.dataset.planName || option.textContent;
          return optionName && optionName.trim() ? optionName.trim() : null;
        }
      }

      return null;
    }

    /**
     * Return a detached, immutable summary of the customer-visible purchase
     * state. Consumers such as the PDP sticky bar must read this snapshot
     * instead of duplicating variant or price calculations.
     *
     * @returns {Readonly<object>}
     */
    function getSnapshot() {
      var v = getSelectedVariant();
      var eff = getEffectivePrice();
      return Object.freeze({
        selectedVariantId: v ? v.id : null,
        selectedVariantTitle: v && v.title != null ? String(v.title) : null,
        purchasable: !!(v && v.purchasable && !quantityInputBlocked && getEffectiveMax() >= 1),
        quantity: Math.max(1, Number(state.quantity) || 1),
        purchaseType: state.isSubscription ? 'subscription' : 'onetime',
        sellingPlanId: state.sellingPlanId,
        sellingPlanName: getSellingPlanName(),
        totalCents: eff.totalCents,
        formattedTotal: fmt(eff.totalCents, moneyFormat)
      });
    }

    function dispatchStateChange() {
      syncDeliveryInfo();
      syncShippingRule();
      container.dispatchEvent(new CustomEvent('variant-selector:state-change', {
        bubbles: true,
        detail: getSnapshot()
      }));
    }

    function syncShippingRule() {
      var variant = getSelectedVariant();
      var valid = variant && variant.purchasable && variant.shippingPolicy === 'de-cart-total'
        && Number(variant.shippingExpiresAt) * 1000 > Date.now()
        && Number(variant.shippingRuleThreshold) > 0 && Number(variant.shippingStandardCents) >= 0;
      var rules = Array.from(container.querySelectorAll('[data-vs-shipping-rule]'));
      var productId = container.dataset.vsProductId;
      if (productId) document.querySelectorAll('[data-vs-shipping-product-id]').forEach(function (rule) {
        if (rule.dataset.vsShippingProductId === productId && !container.contains(rule)) rules.push(rule);
      });
      rules.forEach(function (rule) {
        rule.hidden = !valid;
        if (!valid) return;
        var label = rule.querySelector('[data-vs-shipping-rule-text]');
        if (label) label.textContent = (rule.dataset.vsShippingRuleTemplate || '')
          .replace('__COST__', fmt(Number(variant.shippingStandardCents), moneyFormat))
          .replace('__THRESHOLD__', fmt(Number(variant.shippingRuleThreshold), moneyFormat));
        var subscription = rule.querySelector('[data-vs-shipping-initial-order]');
        if (subscription) subscription.hidden = !state.sellingPlanId;
      });
    }

    // The same Liquid-resolved value feeds the selector and the optional
    // shipping-info block. No second API request or JS business mapping.
    function syncDeliveryInfo() {
      var productId = container.dataset.vsProductId;
      if (!productId) return;
      var variant = getSelectedVariant();
      var text = variant && variant.purchasable && typeof variant.deliveryText === 'string' ? variant.deliveryText : '';
      document.querySelectorAll('[data-delivery-product-id]').forEach(function (block) {
        if (block.dataset.deliveryProductId !== productId) return;
        block.querySelectorAll('[data-shipping-delivery-text]').forEach(function (label) { label.textContent = text; });
        block.querySelectorAll('[data-requires-delivery]').forEach(function (step) { step.hidden = !text; });
        var visibleSteps = 0;
        block.querySelectorAll('.shipping-info__step').forEach(function (step) {
          var arrow = step.previousElementSibling;
          if (arrow && arrow.hasAttribute('data-shipping-arrow')) arrow.hidden = step.hidden || visibleSteps === 0;
          if (!step.hidden) visibleSteps++;
        });
      });
    }

    // --- Quantity list rendering --------------------------------------------
    // Paints [data-vs-quantity-list] with the same markup the legacy
    // size-advisor used (.sml__qty-item radio cards). The mode snippet is
    // responsible for supplying the animal/weight context that informs
    // per-day / reach labels; the controller only needs the selected
    // variant's nominal pack info, which lives on the bridge payload.
    function renderQuantityList() {
      var listEl = query('[data-vs-quantity-list]');
      if (!listEl) return;
      var quantityRadioName = listEl.dataset.vsRadioName || 'sml-qty';
      var v = getSelectedVariant();
      if (!v) { listEl.innerHTML = ''; return; }

      // Optional mode-supplied label context.
      var ctx = options.qtyLabelContext && typeof options.qtyLabelContext === 'function'
        ? (options.qtyLabelContext(v) || {})
        : {};
      var sizeLabel = ctx.sizeLabel || '';
      var packLabel = ctx.packLabel || v.title || '';
      var durationDays = ctx.durationDays != null ? ctx.durationDays : null;

      var popularQty = ctx.popularQty || null;
      var effectiveMax = getEffectiveMax();

      // Live-read merchant settings from the container dataset on every
      // render so theme-editor changes (toggle/label/position) take effect
      // without a page reload. The values passed via `flags` at init time
      // serve only as the initial defaults.
      var liveBadgeShow = container.dataset.freeShippingBadgeShow !== undefined
        ? container.dataset.freeShippingBadgeShow === 'true'
        : freeShippingBadgeShow;
      var liveBadgeLabel = container.dataset.freeShippingBadgeLabel || freeShippingBadgeLabel;
      var liveBadgePosition = container.dataset.freeShippingBadgePosition || freeShippingBadgePosition;

      var html = '';
      for (var q = 1; q <= QTY_PRESET_MAX; q++) {
        if (q > effectiveMax) continue;
        var rowPrice = getPriceForQuantity(q);
        var pct = rowPrice.qtyDiscountPct;
        var baseTotal = rowPrice.purchaseTotalCents;
        var discTotal = rowPrice.totalCents;
        var perDay = (durationDays && durationDays > 0) ? discTotal / (durationDays * q) : null;

        var isSelected = q === state.quantity;
        var cls = 'sml__qty-item' + (isSelected ? ' is-selected' : '');
        var checked = isSelected ? ' checked' : '';

        var popularHtml = (popularQty && q === popularQty)
          ? '<span class="sml__qty-badge--popular">' + escapeHtml(translations.popular || '') + '</span>'
          : '';
        var titleHtml = q + 'x ' + sizeLabel + (sizeLabel ? ' – ' : '') + packLabel;
        var perDaySuffix = translations.perDay || '';
        var perDayHtml = perDay != null
          ? '<div class="sml__qty-perday">ca. ' + fmt(Math.round(perDay), moneyFormat) + ' ' + escapeHtml(perDaySuffix) + '</div>'
          : '';

        var reachHtml = '';
        if (showQtyReach && durationDays && durationDays > 0) {
          var totalDays = Math.round(durationDays * q);
          var daysLabel = totalDays === 1 ? (translations.day || '') : (translations.days || '');
          reachHtml = '<div class="sml__qty-reach">' + escapeHtml(translations.reachPrefix || '') + ' ' + (ctx.emphasizeReach ? '<strong>' : '') + totalDays + ' ' + escapeHtml(daysLabel) + (ctx.emphasizeReach ? '</strong>' : '') + '</div>';
        }

        // Free-shipping badge: storefront hint when the discounted row total
        // reaches the configured threshold. Checkout remains Shopify truth.
        var freeShippingQualifies = false;
        if (liveBadgeShow) {
          freeShippingQualifies = qualifiesForShipping(v, discTotal);
        }
        var freeShippingBadgeHtml = freeShippingQualifies
          ? '<span class="sml__qty-badge--free-shipping" data-vs-free-shipping-badge>' + escapeHtml(liveBadgeLabel) + '</span>'
          : '';
        var badgeRightOfTitle = freeShippingQualifies && liveBadgePosition === 'right_of_title' ? freeShippingBadgeHtml : '';
        var badgeBelowTitle = freeShippingQualifies && liveBadgePosition === 'below_title' ? freeShippingBadgeHtml : '';
        var badgeNextToReach = freeShippingQualifies && liveBadgePosition === 'next_to_reach' ? freeShippingBadgeHtml : '';

        var priceHtml;
        if (pct > 0) {
          priceHtml = '<div class="sml__qty-original">' + fmt(baseTotal, moneyFormat) + '</div>' +
            '<div class="sml__qty-final">' + fmt(discTotal, moneyFormat) + ' <span class="sml__qty-badge">-' + pct + '%</span></div>';
        } else {
          priceHtml = '<div class="sml__qty-final">' + fmt(baseTotal, moneyFormat) + '</div>';
        }

        var titleLineHtml = '<div class="sml__qty-title">' + escapeHtml(titleHtml) +
          (badgeRightOfTitle ? ' ' + badgeRightOfTitle : '') + '</div>';
        if (badgeBelowTitle) {
          titleLineHtml += '<div class="sml__qty-title-badge-row">' + badgeBelowTitle + '</div>';
        }
        var reachLineHtml = reachHtml;
        if (badgeNextToReach) {
          // Inject the badge inline into the reach line, or as a standalone row
          // when reach itself isn't rendered.
          if (reachLineHtml) {
            reachLineHtml = reachLineHtml.replace(
              '<div class="sml__qty-reach">',
              '<div class="sml__qty-reach"><span class="sml__qty-reach-badge-wrap">' + badgeNextToReach + '</span> '
            );
          } else {
            reachLineHtml = '<div class="sml__qty-reach sml__qty-reach--badge-only">' + badgeNextToReach + '</div>';
          }
        }

        var thumbsHtml = '';
        if (ctx.thumbnail && /^(https?:)?\/\//.test(ctx.thumbnail)) {
          thumbsHtml = '<span class="sml__pack-thumbs" aria-hidden="true">';
          for (var thumb = 0; thumb < q; thumb++) thumbsHtml += '<img src="' + escapeHtml(ctx.thumbnail) + '" alt="" width="46" height="65" loading="lazy" decoding="async">';
          thumbsHtml += '</span>';
        }
        html += '<label class="' + cls + '" data-sml-qty="' + q + '" data-vs-qty="' + q + '">' +
          popularHtml +
          '<input type="radio" name="' + escapeHtml(quantityRadioName) + '" value="' + q + '"' + checked + '>' +
          thumbsHtml + '<div class="sml__qty-info">' + titleLineHtml + (ctx.emphasizeReach ? reachLineHtml + perDayHtml : perDayHtml + reachLineHtml) + '</div>' +
          '<div class="sml__qty-prices">' + priceHtml + '</div>' +
        '</label>';
      }

      listEl.innerHTML = html;
      var qtyItems = listEl.querySelectorAll('[data-vs-qty]');
      for (var i = 0; i < qtyItems.length; i++) {
        (function (item) {
          item.addEventListener('click', function () {
            var newQty = Number(item.dataset.vsQty);
            setQuantity(newQty);
            var customEl = query('[data-vs-qty-custom]') || query('[data-sml-qty-custom]');
            if (customEl) customEl.value = '';
          });
        })(qtyItems[i]);
      }
    }

    // --- Price rendering ----------------------------------------------------
    function updatePrice() {
      var v = getSelectedVariant();
      var delivery = query('[data-vs-delivery]');
      if (delivery) {
        var deliveryText = v && v.purchasable && typeof v.deliveryText === 'string' ? v.deliveryText : '';
        var deliveryLabel = delivery.querySelector('[data-vs-delivery-text]');
        if (deliveryLabel) deliveryLabel.textContent = deliveryText;
        delivery.hidden = !deliveryText;
      }
      var btnPrice = query('[data-vs-btn-price]') || query('[data-sml-btn-price]');
      var priceEl = query('[data-vs-price]');
      var grundpreisEl = query('[data-vs-grundpreis]') || query('[data-sml-grundpreis]');
      var priceHistoryEl = query('[data-vs-price-history-note]');
      // Position 'cta_footer': Liquid renders this placeholder only when the
      // theme setting selects the CTA-footer position. Presence in DOM ≡ mode.
      var priceHistoryCtaEl = query('[data-vs-price-history-cta]');
      var positionIsCtaFooter = !!priceHistoryCtaEl;
      var qtyCustomPriceEl = query('[data-vs-custom-qty-price]') || query('[data-sml-qty-custom-price]');
      var qtyCustomEl = query('[data-vs-qty-custom]') || query('[data-sml-qty-custom]');
      var qtyListEl = query('[data-vs-quantity-list]') || query('[data-sml-quantity-list]');
      var freeShippingEl = query('[data-vs-free-shipping]') || query('[data-sml-free-shipping]');

      if (!v) {
        if (btnPrice) btnPrice.textContent = '';
        if (priceEl) priceEl.textContent = '';
        if (grundpreisEl) grundpreisEl.textContent = '';
        if (priceHistoryEl) { priceHistoryEl.textContent = ''; priceHistoryEl.hidden = true; }
        if (priceHistoryCtaEl) { priceHistoryCtaEl.textContent = ''; priceHistoryCtaEl.hidden = true; }
        if (qtyCustomPriceEl) qtyCustomPriceEl.innerHTML = '';
        if (freeShippingEl) freeShippingEl.hidden = true;
        syncAtcDisabled();
        dispatchStateChange();
        return;
      }

      var eff = getEffectivePrice();

      if (btnPrice) btnPrice.textContent = '· ' + fmt(eff.totalCents, moneyFormat);
      if (priceEl) priceEl.textContent = fmt(eff.totalCents, moneyFormat);

      // Keep quantity-list radio highlight in sync when price-only updates happen.
      if (qtyListEl) {
        var items = qtyListEl.querySelectorAll('[data-vs-qty], [data-sml-qty]');
        for (var i = 0; i < items.length; i++) {
          var el = items[i];
          var q = Number(el.dataset.vsQty || el.dataset.smlQty);
          el.classList.toggle('is-selected', q === state.quantity);
          var radio = el.querySelector('input[type="radio"]');
          if (radio) radio.checked = q === state.quantity;
        }
      }

      // Free shipping hint reacts to the full combined discount.
      if (freeShippingEl) {
        var qualifies = false;
        qualifies = qualifiesForShipping(v, eff.totalCents);
        freeShippingEl.hidden = !qualifies;
      }

      // Subscription selector price displays (when present, reflect qty + sub discount).
      var subContainer = query('[data-sml-subscription]');
      if (subContainer) {
        var onetimeTotal = getPriceForQuantity(state.quantity, null).totalCents;
        var onetimeDisplay = subContainer.querySelector('[data-onetime-price]');
        if (onetimeDisplay) onetimeDisplay.textContent = fmt(onetimeTotal, moneyFormat);
        var subTotal = getPriceForQuantity(state.quantity, getSubscriptionPreviewId()).totalCents;
        var subDisplay = subContainer.querySelector('[data-subscription-price]');
        if (subDisplay) subDisplay.textContent = fmt(subTotal, moneyFormat);
        var strikeEl = subContainer.querySelector('[data-original-price]');
        if (strikeEl) strikeEl.textContent = fmt(onetimeTotal, moneyFormat);
        var savingsEl = subContainer.querySelector('[data-savings-amount]');
        if (savingsEl) savingsEl.textContent = fmt(onetimeTotal - subTotal, moneyFormat);
      }

      // Grundpreis line (PAngV) — only when the mode opted in via showUnitPrice.
      // Append the price-history footnote marker (e.g. "**") right after the
      // Grundpreis text when this variant has a valid lower-price-in-30-days
      // signal (Omnibus directive). The note line itself is rendered into a
      // separate sibling element below.
      var hasPriceHistory = !!(v.priceHistory && v.priceHistory.formatted);
      var marker = (translations && translations.priceHistoryMarker) || '';
      if (grundpreisEl && showUnitPrice) {
        var scaled = getUnitPriceTextScaled(v, eff.unitCents);
        var unitPriceTemplate = grundpreisEl.dataset.vsUnitPriceTemplate;
        var grundpreisText = scaled ? (unitPriceTemplate ? unitPriceTemplate.replace('__PRICE__', function () { return scaled; }) : '(' + scaled + ')') : '';
        // Marker stays off the Grundpreis — the price-history reference
        // points to the variant price, not the unit price. (Per-variant
        // mode: Liquid renders the marker at the price line directly.)
        grundpreisEl.textContent = grundpreisText;
      }
      // Per-variant inline note: only when NOT in cta_footer mode.
      if (priceHistoryEl) {
        if (hasPriceHistory && !positionIsCtaFooter && v.priceHistory.note) {
          priceHistoryEl.textContent = v.priceHistory.note;
          priceHistoryEl.hidden = false;
        } else if (hasPriceHistory && !positionIsCtaFooter && translations && translations.priceHistoryNoteTpl) {
          // Legacy fallback: pre-rendered note absent (older variant-list);
          // fall back to the JS-side template substitution.
          priceHistoryEl.textContent = translations.priceHistoryNoteTpl
            .replace('__PRICE__', v.priceHistory.formatted);
          priceHistoryEl.hidden = false;
        } else {
          priceHistoryEl.textContent = '';
          priceHistoryEl.hidden = true;
        }
      }
      // CTA-footer note: only when in cta_footer mode (placeholder exists).
      if (priceHistoryCtaEl) {
        if (hasPriceHistory && v.priceHistory.note) {
          priceHistoryCtaEl.textContent = v.priceHistory.note;
          priceHistoryCtaEl.hidden = false;
        } else {
          priceHistoryCtaEl.textContent = '';
          priceHistoryCtaEl.hidden = true;
        }
      }

      // Custom-qty inline price (opt-in).
      if (qtyCustomPriceEl && showCustomQtyPrice) {
        var customVal = qtyCustomEl ? Number(qtyCustomEl.value) : null;
        if (!quantityInputBlocked && customVal && Math.floor(customVal) === customVal && customVal > 0 && customVal === state.quantity) {
          var baseTotal = eff.purchaseTotalCents;
          // Live-read merchant settings (theme-editor changes take effect
          // without a page reload).
          var liveBadgeShowCustom = container.dataset.freeShippingBadgeShow !== undefined
            ? container.dataset.freeShippingBadgeShow === 'true'
            : freeShippingBadgeShow;
          var liveBadgeLabelCustom = container.dataset.freeShippingBadgeLabel || freeShippingBadgeLabel;
          // Free-shipping qualifier for the custom-qty row: same logic as
          // preset rows. eff.totalCents is already discounted.
          var customQualifies = false;
          if (liveBadgeShowCustom) {
            customQualifies = qualifiesForShipping(v, eff.totalCents);
          }
          var customBadgeHtml = customQualifies
            ? ' <span class="sml__qty-badge--free-shipping" data-vs-free-shipping-badge>' + escapeHtml(liveBadgeLabelCustom) + '</span>'
            : '';
          if (eff.qtyDiscountPct > 0) {
            qtyCustomPriceEl.innerHTML =
              '<span class="sml__qty-original">' + fmt(baseTotal, moneyFormat) + '</span>' +
              '<span class="sml__qty-final">' + fmt(eff.totalCents, moneyFormat) + ' <span class="sml__qty-badge">-' + eff.qtyDiscountPct + '%</span>' + customBadgeHtml + '</span>';
          } else {
            qtyCustomPriceEl.innerHTML = '<span class="sml__qty-final">' + fmt(eff.totalCents, moneyFormat) + customBadgeHtml + '</span>';
          }
        } else {
          qtyCustomPriceEl.innerHTML = '';
        }
      }

      dispatchStateChange();
    }

    // --- Gallery sync -------------------------------------------------------
    // The product gallery is a horizontal scroll-snap track with one slide
    // per media item; each slide carries `data-media-id`. Picking a variant
    // scrolls the matching slide into view — the gallery's own
    // IntersectionObserver keeps indicators + thumbnails in sync. This
    // replaces the old single-slide overwrite which only ever affected
    // slide 0 and went unnoticed when the user had scrolled past it.
    //
    // Fallback path: variant has no media id, or no slide matches (e.g.
    // the variant's media was excluded from the gallery render). Temporarily
    // replace only the current slide and preserve its exact original state so
    // a later matched-variant selection cannot remain stuck on the fallback.
    function captureGalleryImageState(galleryImage) {
      function attribute(name) {
        return {
          present: galleryImage.hasAttribute(name),
          value: galleryImage.getAttribute(name)
        };
      }
      return {
        src: attribute('src'),
        srcset: attribute('srcset'),
        dataSrc: attribute('data-src'),
        dataSrcset: attribute('data-srcset'),
        alt: attribute('alt'),
        aspectRatio: galleryImage.style.aspectRatio,
        deferred: galleryImage.classList.contains('product-gallery__image--deferred')
      };
    }

    function restoreGalleryImageState(galleryImage, state) {
      function restoreAttribute(name, saved) {
        if (saved.present) galleryImage.setAttribute(name, saved.value || '');
        else galleryImage.removeAttribute(name);
      }
      restoreAttribute('src', state.src);
      restoreAttribute('srcset', state.srcset);
      restoreAttribute('data-src', state.dataSrc);
      restoreAttribute('data-srcset', state.dataSrcset);
      restoreAttribute('alt', state.alt);
      galleryImage.style.aspectRatio = state.aspectRatio;
      galleryImage.classList.toggle('product-gallery__image--deferred', state.deferred);
    }

    function restoreVariantFallbackImages(gallery) {
      var restoredImages = [];
      if (!gallery) return restoredImages;
      var galleryImages = gallery.querySelectorAll('.product-gallery__image[data-gallery-main]');
      galleryImages.forEach(function (galleryImage) {
        if (!galleryImage.__drhVariantFallbackOriginal) return;
        restoreGalleryImageState(galleryImage, galleryImage.__drhVariantFallbackOriginal);
        delete galleryImage.__drhVariantFallbackOriginal;
        restoredImages.push(galleryImage);
      });
      return restoredImages;
    }

    function materializeDeferredGalleryImage(galleryImage) {
      if (!galleryImage || !galleryImage.classList.contains('product-gallery__image--deferred')) return;
      if (galleryImage.dataset.srcset) galleryImage.srcset = galleryImage.dataset.srcset;
      if (galleryImage.dataset.src) galleryImage.src = galleryImage.dataset.src;
      galleryImage.removeAttribute('data-src');
      galleryImage.removeAttribute('data-srcset');
      galleryImage.classList.remove('product-gallery__image--deferred');
    }

    function materializeRestoredGalleryImages(restoredImages) {
      restoredImages.forEach(function (galleryImage) {
        materializeDeferredGalleryImage(galleryImage);
      });
    }

    function applyVariantFallbackImage(galleryImage, img) {
      galleryImage.__drhVariantFallbackOriginal = captureGalleryImageState(galleryImage);
      galleryImage.src = img.src;
      if (img.srcset) {
        galleryImage.srcset = img.srcset;
      } else {
        galleryImage.removeAttribute('srcset');
      }
      galleryImage.removeAttribute('data-src');
      galleryImage.removeAttribute('data-srcset');
      galleryImage.classList.remove('product-gallery__image--deferred');
      if (img.alt) galleryImage.alt = img.alt;
      if (img.width && img.height) galleryImage.style.aspectRatio = img.width + ' / ' + img.height;
    }

    function resolveVariantFallbackTarget(gallery) {
      if (!gallery) return null;
      var track = gallery.querySelector('[data-gallery-track]');
      var slides = Array.prototype.slice.call(
        gallery.querySelectorAll('.product-gallery__slide')
      );
      if (!track || !slides.length) return null;

      var currentIndex = track.clientWidth > 0
        ? Math.round(track.scrollLeft / track.clientWidth)
        : 0;
      currentIndex = Math.max(0, Math.min(currentIndex, slides.length - 1));
      var currentSlide = slides[currentIndex];
      var currentImage = currentSlide.querySelector('.product-gallery__image[data-gallery-main]');
      if (currentImage) {
        return { image: currentImage, slide: currentSlide, navigate: false };
      }

      // Video/external-video slides have preview images, but those are not
      // gallery-main images. Use the first real image slide and navigate to it
      // so the newly selected variant image is actually visible.
      for (var index = 0; index < slides.length; index += 1) {
        var image = slides[index].querySelector('.product-gallery__image[data-gallery-main]');
        if (image) return { image: image, slide: slides[index], navigate: true };
      }
      return null;
    }

    function scrollGalleryToSlide(gallery, slide) {
      var track = gallery && gallery.querySelector('[data-gallery-track]');
      if (!track || !slide) return;
      track.scrollTo({
        left: track.scrollLeft + slide.getBoundingClientRect().left - track.getBoundingClientRect().left,
        behavior: galleryScrollBehavior()
      });
    }

    function updateProductGalleryImage() {
      var productRoot = container.closest('.main-product');
      var gallery = productRoot ? productRoot.querySelector('.product-gallery') : null;

      // Always undo an earlier unmatched-media fallback, including when the
      // newly-selected variant has no featured image of its own.
      var restoredImages = restoreVariantFallbackImages(gallery);

      var v = getSelectedVariant();
      if (!v || !v.featuredImage) {
        // A fallback can navigate from a video to an originally deferred image
        // slide. Restoring that slide must not leave a visible GIF placeholder.
        materializeRestoredGalleryImages(restoredImages);
        return;
      }
      var img = v.featuredImage;

      if (img.mediaId) {
        var selectDetail = { mediaId: String(img.mediaId), handled: false };
        if (gallery) {
          gallery.dispatchEvent(new CustomEvent('product-gallery:select-media', {
            detail: selectDetail
          }));
          if (selectDetail.handled) return;
        }

        // Compatibility fallback if the gallery bridge has not initialized yet.
        var slideSel = '.product-gallery__slide[data-media-id="' + String(img.mediaId) + '"]';
        var targetSlide = gallery ? gallery.querySelector(slideSel) : null;
        if (targetSlide) {
          var targetImage = targetSlide.querySelector('.product-gallery__image--deferred');
          if (targetImage) {
            materializeDeferredGalleryImage(targetImage);
          }
          scrollGalleryToSlide(gallery, targetSlide);
          return;
        }
      }

      var fallbackTarget = resolveVariantFallbackTarget(gallery);
      if (!fallbackTarget) return;
      applyVariantFallbackImage(fallbackTarget.image, img);
      if (fallbackTarget.navigate) {
        scrollGalleryToSlide(gallery, fallbackTarget.slide);
      }
    }

    // Effective upper bound for quantity, respecting both the merchant
    // `custom_qty_max` setting (default 20) and Shopify's inventory
    // policy. The JSON bridge emits `inventoryQty` as a number only when
    // the variant is tracked with policy=deny; policy=continue or no
    // tracking → null (= unlimited).
    function getEffectiveMax() {
      var max = customQtyMax;
      var v = getSelectedVariant();
      if (v && typeof v.inventoryQty === 'number' && v.inventoryQty >= 0) {
        max = Math.min(max, v.inventoryQty);
      }
      return max;
    }

    function appendDescribedBy(el, id) {
      if (!el || !id) return;
      var ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (ids.indexOf(id) === -1) ids.push(id);
      el.setAttribute('aria-describedby', ids.join(' '));
    }

    // Keep all quantity affordances aligned with the controller state. This
    // runs even when the numeric value itself did not change, because a
    // variant switch can lower the inventory-backed maximum underneath it.
    function syncQuantityUi(options) {
      options = options || {};
      var preserveCustomInput = !!options.preserveCustomInput;
      var effectiveMax = getEffectiveMax();
      var customInput = query('[data-vs-qty-custom]') || query('[data-sml-qty-custom]');
      var customWrap = query('[data-vs-qty-custom-wrap]')
        || query('[data-sml-qty-custom-wrapper]')
        || (customInput && customInput.parentElement);
      var quantityStatus = query('[data-vs-quantity-status]');

      if (customInput) {
        customInput.min = '1';
        customInput.step = '1';
        customInput.max = String(effectiveMax);
        customInput.disabled = effectiveMax < 4;
        if (quantityStatus && quantityStatus.id) appendDescribedBy(customInput, quantityStatus.id);

        if (!preserveCustomInput) {
          if (state.quantity >= 4 && state.quantity <= effectiveMax) {
            customInput.value = String(state.quantity);
            delete customInput.dataset.vsRoutedPreset;
          } else {
            customInput.value = '';
            customInput.dataset.vsRoutedPreset = 'true';
          }
        }
      }

      if (customWrap) {
        var isPicker = container.dataset.mode === 'picker';
        customWrap.hidden = effectiveMax < 4 || (isPicker && state.quantity <= QTY_PRESET_MAX && !preserveCustomInput);
        customWrap.classList.toggle('vp__qty-custom--active', !customWrap.hidden && state.quantity >= 4);
      }

      var pillButtons = container.querySelectorAll('[data-vs-qty-pills] [data-qty]');
      for (var i = 0; i < pillButtons.length; i++) {
        var pillQty = Number(pillButtons[i].dataset.qty);
        var pillSelected = pillQty === state.quantity && pillQty <= effectiveMax;
        pillButtons[i].classList.toggle('vp__qty-pill--active', pillSelected);
        pillButtons[i].setAttribute('aria-pressed', pillSelected ? 'true' : 'false');
      }

      var quantityItems = container.querySelectorAll('[data-vs-quantity-list] [data-vs-qty], [data-vs-quantity-list] [data-sml-qty]');
      for (var j = 0; j < quantityItems.length; j++) {
        var itemQty = Number(quantityItems[j].dataset.vsQty || quantityItems[j].dataset.smlQty);
        var itemSelected = itemQty === state.quantity && itemQty <= effectiveMax;
        quantityItems[j].classList.toggle('is-selected', itemSelected);
        var radio = quantityItems[j].querySelector('input[type="radio"]');
        if (radio) radio.checked = itemSelected;
      }

      var stepButtons = container.querySelectorAll('[data-vs-qty-step]');
      for (var k = 0; k < stepButtons.length; k++) {
        var currentCustomQty = customInput ? Number(customInput.value) : NaN;
        var isUp = stepButtons[k].dataset.vsQtyStep === 'up';
        stepButtons[k].disabled = effectiveMax < 4
          || (isUp && currentCustomQty >= effectiveMax)
          || (!isUp && currentCustomQty <= 4);
      }
    }

    // --- State mutators -----------------------------------------------------
    function selectVariant(id) {
      id = Number(id);
      if (!id || state.selectedVariantId === id) return;
      state.selectedVariantId = id;

      // Clamp qty to the new variant's effective max — prevents
      // "3 selected, switch variant, 3 still shown but only 2 in stock".
      var newMax = getEffectiveMax();
      var previousQuantity = state.quantity;
      if (state.quantity > newMax) state.quantity = Math.max(1, newMax);
      clearQuantityValidation(true);
      setAtcStatus('', false);

      updateProductGalleryImage();
      renderQuantityList();
      syncQuantityUi();
      updatePrice();
      syncAtcDisabled();

      if (state.quantity !== previousQuantity) {
        setQuantityStatus(formatStatusMessage(getQuantityAdjustedMessage(), {
          QUANTITY: state.quantity,
          MAX: newMax
        }), false);
      }

      emit('change', getState());
    }

    function setQuantity(n, options) {
      options = options || {};
      n = Number(n);
      if (!isFinite(n) || Math.floor(n) !== n) return false;
      if (n < 1) n = 1;
      var maxVal = getEffectiveMax();
      if (n > maxVal) n = Math.max(1, maxVal);
      var wasBlocked = quantityInputBlocked;
      clearQuantityValidation(true);
      setAtcStatus('', false);
      var changed = state.quantity !== n;
      state.quantity = n;
      syncQuantityUi({ preserveCustomInput: !!options.preserveCustomInput });
      syncAtcDisabled();
      if (changed || wasBlocked) {
        updatePrice();
        emit('change', getState());
      }
      return true;
    }

    // User-entered quantities are deliberately stricter than programmatic
    // setQuantity calls: invalid values remain visible for correction and
    // block ATC instead of being silently clamped. Quantities 1–3 select the
    // corresponding preset; commit=false preserves the field while typing.
    function setCustomQuantity(rawValue, options) {
      options = options || {};
      var commit = options.commit !== false;
      var raw = rawValue == null ? '' : String(rawValue).trim();
      var quantity = raw === '' ? NaN : Number(raw);
      var effectiveMax = getEffectiveMax();
      var customInput = query('[data-vs-qty-custom]') || query('[data-sml-qty-custom]');
      var shouldFocusPreset = commit
        && quantity <= QTY_PRESET_MAX
        && container.dataset.mode === 'picker'
        && customInput === document.activeElement;
      var isWholePositive = /^\d+$/.test(raw)
        && isFinite(quantity)
        && Math.floor(quantity) === quantity
        && quantity >= 1;
      setAtcStatus('', false);

      if (!isWholePositive || quantity > effectiveMax) {
        quantityInputBlocked = true;
        if (customInput) delete customInput.dataset.vsRoutedPreset;
        clearCustomQuantityPrices();
        setQuantityStatus(formatStatusMessage(getQuantityInvalidMessage(), {
          MAX: Math.max(1, effectiveMax)
        }), true);
        syncAtcDisabled();
        dispatchStateChange();
        return { valid: false, routedToPreset: false, quantity: null, max: effectiveMax };
      }

      setQuantity(quantity, { preserveCustomInput: !commit });
      if (commit && quantity <= QTY_PRESET_MAX) clearCustomQuantityPrices();
      if (shouldFocusPreset) {
        var preset = query('[data-vs-qty-pills] [data-qty="' + quantity + '"]');
        if (preset && typeof preset.focus === 'function') preset.focus({ preventScroll: true });
      }
      syncAtcDisabled();
      return {
        valid: true,
        routedToPreset: quantity <= QTY_PRESET_MAX,
        quantity: quantity,
        max: effectiveMax
      };
    }

    function setSellingPlan(id) {
      var newId = id == null ? null : Number(id);
      if (state.sellingPlanId === newId) return;
      state.sellingPlanId = newId;
      state.isSubscription = newId != null;
      setAtcStatus('', false);
      renderQuantityList();
      updatePrice();
      updateAtcButtonText();
      emit('change', getState());
    }

    // Returns the label currently appropriate for the ATC button — reflects
    // whether a subscription is selected. Falls back to the generic
    // "In den Warenkorb" if no subscribe-specific translation is provided.
    function currentAtcText() {
      if (state.isSubscription && translations.addToCartSubscribe) {
        return translations.addToCartSubscribe;
      }
      return translations.addToCart || 'In den Warenkorb';
    }
    function updateAtcButtonText() {
      var btnText = query('[data-vs-btn-text]') || query('[data-sml-btn-text]');
      if (btnText) btnText.textContent = currentAtcText();
      // Toggle subscription modifier so CSS can apply the separate
      // `atc_subscription_*` colour-scheme pair when Abo is selected.
      var atcBtn = query('[data-vs-add-to-cart]') || query('[data-sml-add-to-cart]');
      if (atcBtn) atcBtn.classList.toggle('vp__atc-button--subscription', !!state.isSubscription);
    }

    function getState() {
      return {
        selectedVariantId: state.selectedVariantId,
        quantity: state.quantity,
        sellingPlanId: state.sellingPlanId,
        isSubscription: state.isSubscription,
        isFallbackMode: state.isFallbackMode
      };
    }

    // --- Add to cart --------------------------------------------------------
    var atcAttached = false;
    function attachAddToCart() {
      if (atcAttached) return;
      var atc = query('[data-vs-add-to-cart]') || query('[data-sml-add-to-cart]');
      if (!atc) return;
      atcAttached = true;
      atc.addEventListener('click', function () {
        // The public method remains rejecting so direct callers can react to
        // failures. The DOM click boundary owns and consumes that rejection,
        // preventing an unhandled-rejection console error for shoppers.
        performAddToCart().catch(function () {});
      });
    }

    function performAddToCart() {
      if (atcRequest) return atcRequest;
      var v = getSelectedVariant();
      if (!v || !v.purchasable || getEffectiveMax() < 1) return Promise.resolve(null);
      if (quantityInputBlocked) {
        return Promise.reject(new Error('Invalid quantity'));
      }
      var atc = query('[data-vs-add-to-cart]') || query('[data-sml-add-to-cart]');
      var btnText = query('[data-vs-btn-text]') || query('[data-sml-btn-text]');

      if (atcResetTimer) {
        clearTimeout(atcResetTimer);
        atcResetTimer = null;
        if (btnText) btnText.textContent = currentAtcText();
      }

      setAtcStatus('', false);
      atcPending = true;
      if (atc) {
        atc.classList.add('is-loading');
      }
      syncAtcDisabled();
      emit('atc-start', getState());

      var payload = { items: [{ id: v.id, quantity: state.quantity }] };
      if (state.sellingPlanId) payload.items[0].selling_plan = Number(state.sellingPlanId);

      atcRequest = (window.CartAdd
        ? window.CartAdd.add(getCartAddUrl(), payload, atc)
        : Promise.reject(new Error(getCartErrorMessage())))
        .then(function (response) {
          atcRequest = null;
          if (btnText) btnText.textContent = '✓';
          atcResetTimer = setTimeout(function () {
            if (btnText) btnText.textContent = currentAtcText();
            if (atc) {
              atc.classList.remove('is-loading');
            }
            atcPending = false;
            atcResetTimer = null;
            syncAtcDisabled();
          }, 1500);
          emit('atc-success', response);
          return response;
        })
        .catch(function (err) {
          console.error('[VariantSelector] Cart error:', err);
          atcPending = false;
          atcRequest = null;
          if (atc) {
            atc.classList.remove('is-loading');
          }
          var responseMessage = err && err.status && (err.data?.description || err.data?.message);
          setAtcStatus(responseMessage || getCartErrorMessage(), true);
          syncAtcDisabled();
          emit('atc-error', err);
          throw err;
        });
      return atcRequest;
    }

    function setDiscountTiers(raw) {
      var next = parseDiscountTiers(raw);
      if (JSON.stringify(next) === JSON.stringify(discountTiers)) return;
      discountTiers = next;
      var focused = document.activeElement;
      var focusedQuantity = focused && focused.closest('[data-vs-qty], [data-qty]');
      var quantityToRefocus = focusedQuantity && container.contains(focusedQuantity) ? (focusedQuantity.dataset.vsQty || focusedQuantity.dataset.qty) : null;
      var context = query('[data-quantity-price-context]');
      if (context) context.hidden = !next.length;
      renderQuantityList();
      updatePrice();
      emit('discount-change', next);
      emit('change', getState());
      if (quantityToRefocus) {
        var replacement = query('[data-vs-qty="' + quantityToRefocus + '"] input, [data-qty="' + quantityToRefocus + '"]');
        if (replacement) replacement.focus({ preventScroll: true });
      }
    }
    if (nativeDiscounts && window.QuantityDiscountPreview) {
      window.QuantityDiscountPreview.subscribe(container, setDiscountTiers);
    }

    // Public instance API.
    var shippingExpiry = variants.reduce(function (earliest, variant) {
      var value = Number(variant.shippingExpiresAt) * 1000;
      return value > Date.now() && (!earliest || value < earliest) ? value : earliest;
    }, 0);
    if (shippingExpiry) setTimeout(function () {
      if (container.isConnected) { renderQuantityList(); updatePrice(); }
    }, Math.min(2147483647, Math.max(0, shippingExpiry - Date.now() + 10)));

    return {
      getState: getState,
      getSnapshot: getSnapshot,
      getSelectedVariant: getSelectedVariant,
      findVariant: findVariant,
      selectVariant: selectVariant,
      setQuantity: setQuantity,
      setCustomQuantity: setCustomQuantity,
      setSellingPlan: setSellingPlan,
      getEffectivePrice: getEffectivePrice,
      getPriceForQuantity: getPriceForQuantity,
      setDiscountTiers: setDiscountTiers,
      getPurchasePricePayload: getPurchasePricePayload,
      getEffectiveMax: getEffectiveMax,
      renderQuantityList: renderQuantityList,
      updatePrice: updatePrice,
      updateProductGalleryImage: updateProductGalleryImage,
      updateAtcButtonText: updateAtcButtonText,
      attachAddToCart: attachAddToCart,
      performAddToCart: performAddToCart,
      on: on,
      off: off,
      // Expose private setters used by mode snippets to patch state without emitting.
      setFallbackMode: function (on) { state.isFallbackMode = !!on; },
      setSelectedVariantSilent: function (id) { state.selectedVariantId = Number(id) || null; }
    };
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------------------
  // URL sync — shareable links
  // ---------------------------------------------------------------------------
  // Mirrors a subset of the controller state to the current URL via
  // history.replaceState, and applies URL params back to the controller
  // when the page loads. Preserves Shopify's native `?variant=<id>`
  // convention for the variant id (unchanged so server-side
  // `product.selected_or_first_available_variant` still resolves), and
  // uses the `vs_` prefix for our additions to avoid collisions with
  // other apps/scripts:
  //
  //   ?variant=<id>         — Shopify-native, unprefixed
  //   ?vs_quantity=<n>      — clamped against getEffectiveMax on apply
  //   ?vs_plan=<id>         — selling plan id
  //
  // SML-wizard params (`sml_animal`, `sml_form`, `sml_variation`,
  // `sml_weight`) are managed by the SML snippet itself — this helper
  // only handles the variant/quantity/plan triple that's common to both
  // modes.
  //
  // Intended to be called AFTER the mode snippet has done its default
  // initial selectVariant(). That way:
  //   - Fresh load, no URL params → snippet picks first-available, URL stays
  //     clean until the user interacts.
  //   - Load with params → after apply, state matches URL; invalid params
  //     are stripped via a canonicalizing rewrite.
  function attachUrlSync(controller) {
    if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;

    function writeUrl() {
      var s = controller.getState();
      var u = new URL(window.location.href);
      if (s.selectedVariantId) u.searchParams.set('variant', String(s.selectedVariantId));
      else u.searchParams.delete('variant');
      if (s.quantity && s.quantity > 1) u.searchParams.set('vs_quantity', String(s.quantity));
      else u.searchParams.delete('vs_quantity');
      if (s.sellingPlanId) u.searchParams.set('vs_plan', String(s.sellingPlanId));
      else u.searchParams.delete('vs_plan');
      var next = u.pathname + (u.search || '') + (u.hash || '');
      var cur = window.location.pathname + window.location.search + window.location.hash;
      if (next !== cur) window.history.replaceState(null, '', next);
    }

    // Apply URL params. Variant first so setQuantity clamps against the
    // correct variant's effective max.
    var url = new URL(window.location.href);
    var hadParams = false;
    var vParam = url.searchParams.get('variant');
    if (vParam) {
      hadParams = true;
      var vId = Number(vParam);
      if (vId && controller.findVariant(vId)) controller.selectVariant(vId);
    }
    var qParam = url.searchParams.get('vs_quantity');
    if (qParam) {
      hadParams = true;
      var q = Number(qParam);
      if (q >= 1) controller.setQuantity(q);
    }
    var planParam = url.searchParams.get('vs_plan');
    if (planParam) {
      hadParams = true;
      var planId = Number(planParam);
      if (planId) controller.setSellingPlan(planId);
    }

    // If URL had params, canonicalize them (drop invalid variant,
    // clamped quantity, strip qty=1).
    if (hadParams) writeUrl();

    // Subscribe for future changes — variant, quantity, selling plan.
    controller.on('change', writeUrl);
  }

  // ---------------------------------------------------------------------------
  // Public namespace
  // ---------------------------------------------------------------------------
  window.VariantSelector = window.VariantSelector || {};
  window.VariantSelector.fmt = fmt;
  window.VariantSelector.formatUnitPriceCents = formatUnitPriceCents;
  window.VariantSelector.getUnitPriceBase = getUnitPriceBase;
  window.VariantSelector.getUnitPriceText = getUnitPriceText;
  window.VariantSelector.getUnitPriceTextScaled = getUnitPriceTextScaled;
  window.VariantSelector.getDiscountPercent = getDiscountPercent;
  window.VariantSelector.applyPercentDiscount = applyPercentDiscount;
  window.VariantSelector.quantityDiscountedTotal = quantityDiscountedTotal;
  window.VariantSelector.parseDiscountTiers = parseDiscountTiers;
  window.VariantSelector.createController = createController;
  window.VariantSelector.attachUrlSync = attachUrlSync;
  // Exposed so mode snippets (e.g. SML fallback auto-quantity) can clamp
  // their computed best-quantity to the same preset range the controller
  // renders. Keep in sync with the QTY_PRESET_MAX inside createController.
  window.VariantSelector.QTY_PRESET_MAX = 3;
})();
